import { useMutation, useQuery } from '@apollo/client';
import upperFirst from 'lodash/upperFirst';
import React, { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { FeedbackModal, Tag } from '@eluve/blocks';
import { GetFeedbackTagsDocument } from '@eluve/client-gql-operations';
import { HStack, Text, useToast } from '@eluve/components';
import { useIsUserAppointmentOwner } from '@eluve/frontend-appointment-hooks';
import { FeedbackTypeEnum } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

const insertSummaryFeedbackMutation = graphql(`
  mutation insertSummaryFeedback(
    $appointmentId: uuid!
    $comment: String
    $summarySection: String
    $type: FeedbackTypeEnum!
    $llmOutputId: uuid
  ) {
    insertSummaryFeedbackOne(
      object: {
        appointmentId: $appointmentId
        comment: $comment
        summarySection: $summarySection
        type: $type
        llmOutputId: $llmOutputId
      }
    ) {
      id
      type
      __typename
    }
  }
`);

const updateSummaryFeedbackMutation = graphql(`
  mutation updateSummaryFeedback(
    $id: uuid!
    $comment: String
    $summarySection: String
    $rating: Int
  ) {
    updateSummaryFeedback(
      where: { id: { _eq: $id } }
      _set: {
        comment: $comment
        summarySection: $summarySection
        rating: $rating
      }
    ) {
      affectedRows
    }
  }
`);

const insertSummaryFeedbackTagsMutation = graphql(`
  mutation insertSummaryFeedbackTags(
    $feedbackTags: [SummaryFeedbackTagsInsertInput!]!
  ) {
    insertSummaryFeedbackTags(objects: $feedbackTags) {
      affectedRows
    }
  }
`);

export interface SummaryFeedbackModalProps {
  appointmentId: string;
  llmOutputId: string | null;
  summarySection?: string;
}

export type SummaryFeedbackTags = Tag & { type: FeedbackTypeEnum | null };

export const SummaryFeedbackModal: React.FC<SummaryFeedbackModalProps> = ({
  appointmentId,
  summarySection,
  llmOutputId,
}) => {
  const toast = useToast();
  const apiClient = useApiClient();
  const tenantId = useAssignedTenantIdFromParams();
  const isAppointmentOwner = useIsUserAppointmentOwner();

  const [feedbackScoreId, setFeedbackScoreId] = useState<string | null>(null);
  const [tags, setTags] = useState<SummaryFeedbackTags[]>([]);
  const [selectedFeedbackType, setFeedbackType] =
    useState<FeedbackTypeEnum | null>(null);

  const [feedbackId, setFeedbackId] = useState<string>();

  const { data: getFeedbackTagsData } = useQuery(GetFeedbackTagsDocument);

  const [insertFeedbackMutation] = useMutation(insertSummaryFeedbackMutation, {
    onError: () => toast.error('Failed to save feedback'),
  });

  const [updateFeedbackMutation] = useMutation(updateSummaryFeedbackMutation, {
    onError: () => toast.error('Failed to update feedback'),
  });

  const [insertFeedbackTagMutation] = useMutation(
    insertSummaryFeedbackTagsMutation,
    {
      onError: () => toast.error('Failed to update feedback tags'),
    },
  );

  useEffect(() => {
    setTags(
      getFeedbackTagsData?.feedbackTags?.map((tag) => ({
        ...tag,
        isTagged: false,
      })) ?? [],
    );
  }, [getFeedbackTagsData]);

  const insertFeedback = async (
    type: FeedbackTypeEnum,
    comment?: string,
    section?: string,
  ) => {
    const { data } = await insertFeedbackMutation({
      variables: {
        appointmentId,
        type,
        comment: comment ?? null,
        summarySection: section ?? null,
        llmOutputId,
      },
    });

    const feedbackId = data?.insertSummaryFeedbackOne?.id;

    setFeedbackId(feedbackId);

    return feedbackId;
  };

  const onFeedbackClick = async (type: FeedbackTypeEnum) => {
    setFeedbackType(type);

    // save thumbs up / down feedback
    await insertFeedback(type);

    if (llmOutputId) {
      const result = await apiClient.llmOutputs.addUserFeedbackScore({
        params: { tenantId, llmOutputId },
        body: { value: type },
      });
      if (result.status === 201) {
        setFeedbackScoreId(result.body.scoreId);
      }
    }
  };

  const onTagPressedChange = async (tagId: number, isTagged: boolean) => {
    setTags(
      tags.map((t) => {
        if (t.id === tagId) {
          return { ...t, isTagged };
        }
        return t;
      }),
    );
  };

  const onAdditionalFeedbackSubmit = async (
    type: FeedbackTypeEnum | null,
    comment: string,
    tags: Tag[],
    rating: number | null,
  ) => {
    let summaryFeedbackId = feedbackId;

    if (summaryFeedbackId) {
      await updateFeedbackMutation({
        variables: {
          id: summaryFeedbackId,
          comment,
          summarySection: summarySection ?? null,
          rating,
        },
      });
    } else if (selectedFeedbackType) {
      summaryFeedbackId = await insertFeedback(
        selectedFeedbackType,
        comment,
        summarySection,
      );
    }

    if (llmOutputId && type) {
      if (feedbackScoreId) {
        await apiClient.llmOutputs.updateUserFeedbackScore({
          params: { tenantId, llmOutputId, scoreId: feedbackScoreId },
          body: { value: type, comment: comment ?? null },
        });
      } else {
        const result = await apiClient.llmOutputs.addUserFeedbackScore({
          params: { tenantId, llmOutputId },
          body: { value: type, comment: comment ?? null },
        });
        if (result.status === 201) {
          setFeedbackScoreId(result.body.scoreId);
        }
      }

      if (rating !== null) {
        await apiClient.llmOutputs.addUserRatingScore({
          params: { tenantId, llmOutputId },
          body: { value: rating, comment: comment ?? null },
        });
      }
    }

    const selectedTags = tags.filter((tag) => tag.isTagged);

    const feedbackTagsInput = selectedTags.map((tag) => ({
      summaryFeedbackId,
      feedbackTagId: tag.id,
    }));

    await insertFeedbackTagMutation({
      variables: {
        feedbackTags: feedbackTagsInput,
      },
    });

    if (llmOutputId) {
      const feedbackTagPromises = selectedTags.map((tag) =>
        apiClient.llmOutputs.addUserFeedbackTagScore({
          params: { tenantId, llmOutputId },
          body: { value: upperFirst(tag.name) },
        }),
      );

      await Promise.allSettled(feedbackTagPromises);
    }

    const message =
      type === 'POSITIVE'
        ? 'Thanks for the feedback! Glad you liked this one!'
        : 'Sorry that this summary missed the mark. Every piece of feedback you give (positive or negative) helps us improve our future results.';
    toast.success(message);
  };

  const content = (
    <HStack wFull justify="end" align="center">
      <Text color="secondary" size="md" weight="regular">
        {`Was this note helpful?`}
      </Text>
      <FeedbackModal
        feedbackType={selectedFeedbackType}
        tags={[
          ...tags.filter((tag) => tag.type === selectedFeedbackType),
          ...tags.filter((tag) => tag.type !== selectedFeedbackType),
        ]}
        onFeedbackClick={onFeedbackClick}
        onTagPressedChange={onTagPressedChange}
        onAdditionalFeedbackSubmit={onAdditionalFeedbackSubmit}
      />
    </HStack>
  );

  if (!isAppointmentOwner) {
    return null;
  }

  return content;
};
